import React, { useEffect, useState } from 'react';
import Modal from '../UI/Modal';
import Button from '../UI/Button';
import { TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import gameRoomService, {
  GameRoom,
  ScheduleSlot,
} from '../../api/services/gameRoom.service';

interface GameRoomFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  room?: GameRoom | null; // null / undefined → create mode
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const defaultPatterns = {
  horizontal: true,
  vertical: true,
  diagonal: true,
  singleCorner: false,
  singleMiddle: false,
  middleCross: false,
  full: true,
};

const GameRoomFormModal: React.FC<GameRoomFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  room,
}) => {
  const isEdit = !!room;

  const [roomId, setRoomId] = useState('');
  const [betAmount, setBetAmount] = useState<number | ''>(10);
  const [description, setDescription] = useState('');
  const [rules, setRules] = useState('');
  const [commissionPercent, setCommissionPercent] = useState<number | ''>(15);
  const [callIntervalSeconds, setCallIntervalSeconds] = useState<number | ''>(5);
  const [patternsRequiredToWin, setPatternsRequiredToWin] = useState<number | ''>(1);
  const [maxCartelaCount, setMaxCartelaCount] = useState<number | ''>(500);
  const [isSuperBingo, setIsSuperBingo] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [schedule, setSchedule] = useState<ScheduleSlot[]>([]);
  const [scheduledStartAt, setScheduledStartAt] = useState('');
  const [patterns, setPatterns] = useState(defaultPatterns);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hydrate when opening
  useEffect(() => {
    if (!isOpen) return;
    if (room) {
      setRoomId(room.roomId);
      setBetAmount(room.betAmount);
      setDescription(room.description || '');
      setRules(room.rules || '');
      setCommissionPercent(room.commissionPercent);
      setCallIntervalSeconds(room.callIntervalSeconds);
      setPatternsRequiredToWin(room.patternsRequiredToWin);
      setMaxCartelaCount(room.maxCartelaCount ?? 500);
      setIsSuperBingo(room.isSuperBingo);
      setIsActive(room.isActive);
      setSchedule(room.schedule || []);
      setScheduledStartAt(
        room.scheduledStartAt
          ? new Date(room.scheduledStartAt).toISOString().slice(0, 16)
          : ''
      );
      setPatterns({ ...defaultPatterns, ...room.patterns });
    } else {
      setRoomId('');
      setBetAmount(10);
      setDescription('');
      setRules('');
      setCommissionPercent(15);
      setCallIntervalSeconds(5);
      setPatternsRequiredToWin(1);
      setMaxCartelaCount(500);
      setIsSuperBingo(false);
      setIsActive(true);
      setSchedule([]);
      setScheduledStartAt('');
      setPatterns(defaultPatterns);
    }
    setError(null);
  }, [isOpen, room]);

  const addScheduleSlot = () =>
    setSchedule((s) => [...s, { day: 0, hour: 16, minute: 0 }]);

  const updateScheduleSlot = (i: number, patch: Partial<ScheduleSlot>) =>
    setSchedule((s) =>
      s.map((slot, idx) => (idx === i ? { ...slot, ...patch } : slot))
    );

  const removeScheduleSlot = (i: number) =>
    setSchedule((s) => s.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    setError(null);

    if (!roomId.trim()) return setError('Room ID is required');
    if (betAmount === '' || Number(betAmount) <= 0)
      return setError('Bet amount must be greater than 0');
    if (maxCartelaCount === '' || Number(maxCartelaCount) <= 0)
      return setError('Max cartela count must be greater than 0');

    if (isSuperBingo && schedule.length === 0)
      return setError('Super Bingo rooms require at least one schedule slot');

    setSaving(true);
    try {
      const payload = {
        betAmount: Number(betAmount),
        description,
        rules: rules || null,
        commissionPercent: Number(commissionPercent) || 0,
        callIntervalSeconds: Number(callIntervalSeconds) || 5,
        patternsRequiredToWin: Number(patternsRequiredToWin) || 1,
        maxCartelaCount: Number(maxCartelaCount) || 500,
        isSuperBingo,
        isActive,
        schedule: isSuperBingo ? schedule : [],
        scheduledStartAt: isSuperBingo && scheduledStartAt
          ? new Date(scheduledStartAt).toISOString()
          : null,
        patterns,
      };

      if (isEdit && room) {
        await gameRoomService.update(room._id, payload);
      } else {
        await gameRoomService.create({
          roomId: roomId.trim(),
          ...payload,
        });
      }

      onSuccess();
      onClose();
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          'Failed to save game room'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Game Room' : 'Create Game Room'}
      size="lg"
    >
      <div className="space-y-4">
        {error && (
          <div className="text-xs bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        {/* Basic fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Room ID
            </label>
            <input
              type="text"
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
              disabled={isEdit}
              placeholder="room-10"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:bg-gray-50 disabled:text-gray-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Bet Amount (ETB)
            </label>
            <input
              type="number"
              min={1}
              value={betAmount}
              onChange={(e) =>
                setBetAmount(e.target.value === '' ? '' : Number(e.target.value))
              }
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Commission %
            </label>
            <input
              type="number"
              min={0}
              max={100}
              value={commissionPercent}
              onChange={(e) =>
                setCommissionPercent(
                  e.target.value === '' ? '' : Number(e.target.value)
                )
              }
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Call Interval (seconds)
            </label>
            <input
              type="number"
              min={1}
              value={callIntervalSeconds}
              onChange={(e) =>
                setCallIntervalSeconds(
                  e.target.value === '' ? '' : Number(e.target.value)
                )
              }
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Patterns Required To Win
            </label>
            <input
              type="number"
              min={1}
              max={10}
              value={patternsRequiredToWin}
              onChange={(e) =>
                setPatternsRequiredToWin(
                  e.target.value === '' ? '' : Number(e.target.value)
                )
              }
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Max Cartela Count
            </label>
            <input
              type="number"
              min={1}
              value={maxCartelaCount}
              onChange={(e) =>
                setMaxCartelaCount(
                  e.target.value === '' ? '' : Number(e.target.value)
                )
              }
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          <div className="flex items-center gap-3 pt-5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="w-4 h-4 accent-primary-500"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isSuperBingo}
                onChange={(e) => setIsSuperBingo(e.target.checked)}
                className="w-4 h-4 accent-primary-500"
              />
              <span className="text-sm text-gray-700">Super Bingo</span>
            </label>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Rules (optional)
          </label>
          <input
            type="text"
            value={rules}
            onChange={(e) => setRules(e.target.value)}
            placeholder="e.g. Full house only"
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>

        {/* Super Bingo-only section */}
        {isSuperBingo && (
          <div className="border border-primary-200 bg-primary-50/40 rounded-lg p-3 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold text-primary-700 uppercase tracking-wide">
                Super Bingo Schedule
              </h4>
              <button
                onClick={addScheduleSlot}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
              >
                <PlusIcon className="w-3.5 h-3.5" />
                Add Slot
              </button>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Scheduled Start (optional)
              </label>
              <input
                type="datetime-local"
                value={scheduledStartAt}
                onChange={(e) => setScheduledStartAt(e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>

            {schedule.length === 0 && (
              <p className="text-xs text-gray-500">
                No slots yet — click "Add Slot" to create one.
              </p>
            )}

            <div className="space-y-2">
              {schedule.map((slot, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 bg-white border border-gray-100 rounded-lg px-2 py-2"
                >
                  <select
                    value={slot.day}
                    onChange={(e) =>
                      updateScheduleSlot(i, { day: Number(e.target.value) })
                    }
                    className="text-xs border border-gray-200 rounded-md px-2 py-1"
                  >
                    {DAYS.map((d, idx) => (
                      <option key={idx} value={idx}>
                        {d}
                      </option>
                    ))}
                  </select>

                  <input
                    type="number"
                    min={0}
                    max={23}
                    value={slot.hour}
                    onChange={(e) =>
                      updateScheduleSlot(i, { hour: Number(e.target.value) })
                    }
                    className="w-16 text-xs border border-gray-200 rounded-md px-2 py-1"
                  />
                  <span className="text-xs text-gray-400">:</span>
                  <input
                    type="number"
                    min={0}
                    max={59}
                    value={slot.minute}
                    onChange={(e) =>
                      updateScheduleSlot(i, { minute: Number(e.target.value) })
                    }
                    className="w-16 text-xs border border-gray-200 rounded-md px-2 py-1"
                  />

                  <button
                    onClick={() => removeScheduleSlot(i)}
                    className="ml-auto p-1 rounded hover:bg-red-50 text-red-500"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Patterns */}
        <div>
          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
            Winning Patterns
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {(Object.keys(patterns) as (keyof typeof patterns)[]).map((key) => (
              <label
                key={key}
                className="flex items-center gap-2 text-xs bg-gray-50 border border-gray-100 rounded-lg px-2 py-1.5 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={patterns[key]}
                  onChange={(e) =>
                    setPatterns({ ...patterns, [key]: e.target.checked })
                  }
                  className="w-3.5 h-3.5 accent-primary-500"
                />
                <span className="capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Room'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default GameRoomFormModal;